import { useEffect, useState } from "react";
import { CalendarClock, ChevronRight, ChevronDown, Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  } catch { return "—"; }
}

function RenewalBadge({ days }) {
  if (days == null) return <span className="beacon-badge bg-slate-100 text-slate-500 border-slate-200">no date</span>;
  if (days < 0) return <span className="beacon-badge bg-rose-50 text-rose-700 border-rose-200"><span className="beacon-dot bg-rose-600" /> {Math.abs(days)}d overdue</span>;
  if (days <= 7) return <span className="beacon-badge bg-amber-50 text-amber-800 border-amber-200"><span className="beacon-dot bg-amber-500" /> {days}d left</span>;
  if (days <= 30) return <span className="beacon-badge bg-blue-50 text-blue-700 border-blue-200"><span className="beacon-dot bg-blue-500" /> {days}d left</span>;
  return <span className="beacon-badge bg-emerald-50 text-emerald-700 border-emerald-200"><span className="beacon-dot bg-emerald-500" /> {days}d left</span>;
}

export function UpcomingRenewalsCard({ defaultOpen = false, days = 30 }) {
  const [upcoming, setUpcoming] = useState([]);
  const [open, setOpen] = useState(defaultOpen);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const goToCustomer = (u) => {
    // Prefer the first service in the group so we can land on the correct tab.
    const svc = (u.services && u.services[0]?.service_type) || u.service_type || "hosting";
    const name = u.customer_name || "";
    navigate(`/subscriptions?service=${encodeURIComponent(svc)}&company=${encodeURIComponent(name)}`);
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/subscriptions/upcoming?days=${days}`);
      setUpcoming(r.data);
    } catch (err) {
      // non-fatal: card just hides
      console.warn("Could not load upcoming renewals:", err);
    }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [days]);

  const sendReminder = async (u) => {
    const ids = u.subscription_ids || [u.id];
    const many = ids.length > 1;
    const confirmMsg = many
      ? `Send one combined renewal reminder to ${u.customer_email} covering ${ids.length} services?`
      : `Send a renewal reminder email to ${u.customer_email}?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      if (many) {
        await api.post(`/subscriptions/bulk-reminder`, { subscription_ids: ids });
      } else {
        await api.post(`/subscriptions/${ids[0]}/reminder`);
      }
      toast.success(`Reminder sent to ${u.customer_email}`);
    } catch (err) {
      toast.error("Could not send reminder", { description: formatApiError(err) });
    }
  };

  const total = upcoming.length;
  const overdue = upcoming.filter((u) => (u.days_until_renewal ?? 0) < 0).length;
  const dueSoon = upcoming.filter((u) => (u.days_until_renewal ?? 999) >= 0 && (u.days_until_renewal ?? 999) <= 7).length;

  if (loading && total === 0) return null; // hide while first-loading to avoid layout jump
  if (!loading && total === 0) return null; // nothing due — reclaim the vertical space

  // Tint the whole card based on urgency: overdue → amber, else soft blue
  const tone = overdue > 0
    ? {
        card: "border-amber-200 bg-gradient-to-r from-amber-50 via-amber-50/40 to-white",
        icon: "bg-amber-100 border-amber-200 text-amber-700",
        hover: "hover:bg-amber-50/60",
      }
    : {
        card: "border-blue-200 bg-gradient-to-r from-blue-50 via-blue-50/40 to-white",
        icon: "bg-blue-100 border-blue-200 text-blue-700",
        hover: "hover:bg-blue-50/60",
      };

  return (
    <div className={`beacon-card overflow-hidden ${tone.card}`} data-testid="upcoming-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={total === 0}
        className={`w-full flex items-center gap-3 px-5 sm:px-6 py-4 text-left transition-colors ${total === 0 ? "cursor-default" : tone.hover}`}
        data-testid="upcoming-toggle-btn"
        aria-expanded={open}
      >
        <div className={`w-9 h-9 rounded-xl border grid place-items-center shrink-0 ${tone.icon}`}>
          <CalendarClock className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-slate-900 leading-tight">Upcoming renewals this month</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {total === 0 ? (
              <>Nothing due in the next {days} days.</>
            ) : (
              <>
                <span className="font-mono font-semibold text-slate-900" data-testid="upcoming-total-count">{total}</span> due
                {overdue > 0 && <> · <span className="text-rose-700 font-semibold">{overdue}</span> overdue</>}
                {dueSoon > 0 && <> · <span className="text-amber-700 font-semibold">{dueSoon}</span> this week</>}
              </>
            )}
          </div>
        </div>
        {total > 0 && (
          open
            ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
            : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
        )}
      </button>
      {open && total > 0 && (
        <div className="px-5 sm:px-6 pb-5 pt-1 border-t border-slate-100">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="upcoming-renewals-list">
            {upcoming.map((u) => {
              const svc = u.services || [{ service_type: u.service_type, id: u.id }];
              // Compact type-summary: "Hosting (2) · Email (1)"
              const counts = svc.reduce((acc, s) => {
                const t = (s.service_type || "").toLowerCase();
                acc[t] = (acc[t] || 0) + 1;
                return acc;
              }, {});
              const TYPE_LABEL = { hosting: "Hosting", email: "Email", ssl: "SSL", server: "Server" };
              const typeOrder = ["hosting", "email", "ssl", "server"];
              const typeSummary = typeOrder
                .filter((t) => counts[t])
                .map((t) => `${TYPE_LABEL[t] || t} (${counts[t]})`)
                .join(" · ");
              const many = svc.length > 1;
              return (
                <div key={u.id} className="rounded-xl border border-slate-200 bg-slate-50/50 p-3" data-testid={`upcoming-${u.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() => goToCustomer(u)}
                        data-testid={`upcoming-open-${u.id}`}
                        className="font-semibold text-slate-900 text-sm truncate text-left hover:text-blue-700 hover:underline underline-offset-2"
                        title="Open in Subscriptions"
                      >
                        {u.customer_name}
                      </button>
                      <div className="text-[11px] text-slate-600 font-mono mt-0.5 truncate" data-testid={`upcoming-types-${u.id}`}>
                        {typeSummary}
                      </div>
                    </div>
                    <RenewalBadge days={u.days_until_renewal} />
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-[11px] text-slate-500">Renews {formatDate(u.renewal_date)}</span>
                    <button
                      onClick={() => sendReminder(u)}
                      data-testid={`upcoming-remind-${u.id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:text-blue-800 hover:bg-blue-50 rounded px-1.5 py-0.5"
                    >
                      <Bell className="w-3 h-3" /> {many ? `Remind (${svc.length})` : "Remind"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
